import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { makeStyles } from '@material-ui/core';
import {
  BaseButton,
  FormikField,
  BaseCardField,
  BaseInputLabel,
  BaseTypography,
} from 'src/components/common';
import { useFormikErrorsHandler } from 'src/utils/hooks/useFormikErrorsHandler';
import { newContactValidationSchema } from './newContactValidationSchema';
import { ExpertisesMultiSelect } from 'src/components/fields/selects/ExpertisesMultiSelect';
import { AvailableForCheckboxesContainer } from '../../../../components/fields/other/AvailableForCheckboxes/AvailableForCheckboxesContainer';
import { CountriesMultiSelect } from 'src/components/fields/selects/CountriesMultiSelect';
import { Rating } from '@material-ui/lab';
import { ICreateGuestModel, IGuestInfo } from 'src/api/base/Guest/types';
import { IBaseForm } from 'src/utils/types/forms';
import { BaseButtonGroup } from '../../../../components/common/BaseButton/BaseButtonGroup';
import { FilesUploader } from 'src/components/common/FilesUploader';
import { BaseHelperText } from '../../../../components/common/BaseTextField/BaseHelperText/BaseHelperText';
import { capitalizeFirstLetter, getReadableDate } from 'src/utils/helpers';
import { GenderSelect } from '../../../../components/fields/selects/GenderSelect';
import { LanguagesMultiSelect } from 'src/components/fields/selects/LanguagesMultiSelect/LanguagesMultiSelect';
import { BaseDateTimePicker } from 'src/components/common/BaseDateTimePicker/BaseDateTimePicker';
import clsx from 'clsx';
import { IOSSwitch } from 'src/components/common/BaseButton/IosSwitch';

const useStyles = makeStyles((theme) => ({
  formGroups: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: '32px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
    '& label p': {
      color: '#6A7282 !important',
    },
    '& input': {
      color: '#000000 !important',
    },
    '& .MuiSelect-select span': {
      color: '#000000 !important',
    },
    '& .MuiRating-icon.MuiRating-iconFilled .MuiSvgIcon-root': {
      fill: '#FDB44B !important',
    },
    '& .MuiRating-icon.MuiRating-iconEmpty .MuiSvgIcon-root': {
      fill: '#D1D5DC !important',
    },
  },
  formGroups2: {
    gridTemplateColumns: '324px 1fr',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  formGroups3: {
    gridTemplateColumns: '1fr 1fr',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  formGroup: {
    display: 'grid',
    gridGap: '20px',
    gridAutoRows: 'max-content',
  },
  submitSection: {
    marginTop: 'auto',
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(100px, 200px)',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    /* position: 'absolute',
    top: '40px',
    right: '35px', */
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
      gridGap: '20px',
    },
  },
  available: {
    'boxShadow': 'none',
    'padding': '0 !important',
	'& .MuiFormControlLabel-label': {
      fontSize: '14px !important',
    }
  },
  borderBottomLine: {
    paddingBottom: '24px',
    borderBottom: '1px solid #E5E7EB',
    marginBottom: 24,
  },
}));

type PNewContactForm = IBaseForm<ICreateGuestModel> & {
  guest?: IGuestInfo | null;
  isCreatingForm?: boolean;
  submitBtnText?: string;
};

const formInitialValues = {
  first_name: '',
  last_name: '',
  gender: '',
  title: '',
  company_name: '',
  linkedin: '',
  expertise: [],
  available: [],
  country: [],
  description: '',
  rating: 4,
  is_company_logo_visible: false,
  is_company_description_visible: false,

  guest_company_description: '',
  guest_company_logo: '',
  manager_company_description: '',
  manager_company_logo: '',
  media_license_expiry: '',
  training_certificate: '',
  languages: [],
};

const formKeys = Object.keys(formInitialValues);

export const NewContactForm: React.FC<PNewContactForm> = (props) => {
  const {
    onSubmit,
    loading,
    errors,
    guest,
    isCreatingForm,
    submitBtnText = 'Save',
  } = props;

  const classes = useStyles();
  const formik = useFormik<ICreateGuestModel>({
    initialValues: { ...formInitialValues },
    validationSchema: newContactValidationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      const localGuest = guest || {};
      onSubmit({ ...localGuest, ...values });
      formik.setErrors({});
    },
  });
  const { setFieldValue } = formik;

  useEffect(() => {
    if (guest) {
      const arrKeys = ['expertise', 'available', 'country', 'languages'];
      formKeys.forEach((key) => {
        if (!isCreatingForm && arrKeys.includes(key)) {
          // @ts-ignore
          const val = guest[key].map((el: any) => el.id);
          setFieldValue(key, val);
        } else if (key === 'guest_company_description') {
          setFieldValue(key, guest[key] || '');
        } else {
          // @ts-ignore
          setFieldValue(key, guest[key]);
        }
      });
    }
  }, [guest, isCreatingForm, setFieldValue]);

  const { setFieldError } = formik;

  useFormikErrorsHandler({ errors, setFieldError });

  const [hover, setHover] = React.useState<number>(5);

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <div className={clsx(classes.formGroups, classes.borderBottomLine)}>
          <div className={classes.formGroup}>
            <FormikField
              label="First name*"
              name="first_name"
              formik={formik}
            />
            <FormikField label="Last name*" name="last_name" formik={formik} />
            <FormikField label="Title*" name="title" formik={formik} />
            <FormikField
              label="Company name*"
              name="company_name"
              formik={formik}
            />
          </div>
          <div className={classes.formGroup}>
            <FormikField
              label="Gender*"
              name="gender"
              formik={formik}
              inputComponent={GenderSelect}
              onChange={(newValue: string) => {
                formik.setFieldValue('gender', newValue);
              }}
            />
            <FormikField
              inputComponent={CountriesMultiSelect}
              name="country"
              formik={formik}
              label="Country of residence*"
              onChange={(value: any) => {
                formik.setFieldValue('country', value);
              }}
            />
            <FormikField
              label="Languages"
              name="languages"
              formik={formik}
              inputComponent={LanguagesMultiSelect}
              onChange={(value: any) => {
                formik.setFieldValue('languages', value);
              }}
            />
            <FormikField
              label="LinkedIn link*"
              name="linkedin"
              formik={formik}
            />
          </div>
        </div>
        <div>
          <BaseTypography style={{ color: '#4A9EDB', fontSize: '18px', fontWeight: '600', marginBottom: '24px', lineHeight: 'normal' }} variant="h3" >
            Key Information
          </BaseTypography>
          <div className={clsx(classes.formGroups, classes.formGroups2)}>
          <div className={classes.formGroup}>
              <FormikField
                label="Media strategy"
                name="cba_lead"
                formik={formik}
				disabled
              />
              <div >
                <div style={{ marginBottom: '8px', color: '#6A7282' }}>Last date trained</div>
                <BaseDateTimePicker
                  format="yyyy-MM-dd"
                  errorText={formik.errors.last_date_trained as string}
                  error={Boolean(formik.errors.last_date_trained)}
                  placeholder="Select date and time"
                  value={(formik?.values?.last_date_trained && formik?.values?.last_date_trained !== '0000-00-00') && formik?.values?.last_date_trained}
                  disabled
                />
              </div>
              <div >
                <div style={{ marginBottom: '8px', color: '#6A7282' }}>Media License Expiry</div>
                <BaseDateTimePicker
                  format="yyyy-MM-dd"
                  errorText={formik.errors.media_license_expiry as string}
                  error={Boolean(formik.errors.media_license_expiry)}
                  placeholder="Select date and time"
                  value={(formik?.values?.media_license_expiry && formik?.values?.media_license_expiry !== '0000-00-00') && formik?.values?.media_license_expiry}
                  disabled
                />
              </div>

            </div>
            <div className={classes.formGroup}>
              <BaseCardField
                label="Rating"
                errorText={formik.errors.rating}
                cardOptions={{ offCardStyles: true }}>
                <Rating
                  name="rating"
                  size="large"
                  value={formik.values.rating}
                  precision={1}
                  onChangeActive={(_, newHover) => {
                    setHover(newHover);
                  }}
                  onClick={() => {
                    formik.setFieldValue('rating', hover);
                  }}
                />
              </BaseCardField>
              <AvailableForCheckboxesContainer
                value={formik.values.available}
                errorText={formik.errors.available}
                onChange={(value: any) => {
                  formik.setFieldValue('available', value);
                }}
                offCardStyles={false}
                className={classes.available}
              />
              <FormikField
                name="expertise"
                formik={formik}
                label="Areas of expertise *"
                inputComponent={ExpertisesMultiSelect}
                onChange={(value: any) => {
                  formik.setFieldValue('expertise', value);
                }}
              />
            </div>
          </div>
          <div className={classes.borderBottomLine}>
            <p>Unique insights*</p>
                <FormikField
                  multiline
                  rowsMin={6}
                  rows={6}
                  name="description"
                  formik={formik}
                  InputProps={{
                    style: {
                      height: '136px',
                    },
                  }}
                />
                <p style={{ textAlign: 'right', color: '#8E8E93' }}>
                  Characters remaining:{' '}
                  {510 - formik.values?.description?.length < 0
                    ? '0'
                    : 510 - formik.values?.description?.length}
                </p>
          </div>
        </div>
        <div>
          <BaseTypography style={{ color: '#4A9EDB', fontSize: '18px', fontWeight: '600', marginBottom: '24px', lineHeight: 'normal' }} variant="h3" >
            Company Information
          </BaseTypography>
          {formik.values.is_company_logo_visible && (
              <>
                <div>
                  <BaseInputLabel>Company logo*</BaseInputLabel>
                  <FilesUploader
                    accept="image/*"
                    onChooseFile={(files) => {
                      formik.setFieldValue(
                        'guest_company_logo',
                        files.length ? files[0] : '',
                      );
                    }}
                    files={formik.values.guest_company_logo}
                    multiple={false}
                  />
                  {formik.errors.guest_company_logo && (
                    <BaseHelperText error>
                      {capitalizeFirstLetter(
                        formik.errors.guest_company_logo || '',
                      )}
                    </BaseHelperText>
                  )}
                </div>
              </>
            )}
            { /* <BaseButtonGroup
              value={formik.values.is_company_logo_visible}
              label="Show company logo in profile"
              onChange={(value) => {
                formik.setFieldValue('is_company_logo_visible', value);
              }}
            /> */ }
            <BaseTypography variant="caption" className="mt-8" style={{ margin: '16px 0' }}>
              Show in profile?<span><IOSSwitch name="is_company_logo_visible" checked={formik.values.is_company_logo_visible} onChange={formik.handleChange} /></span>
            </BaseTypography>
            {formik.values.is_company_description_visible && (
              <FormikField
                label="Company Description*"
                name="guest_company_description"
                formik={formik}
                multiline
                rowsMin={6}
                rows={6}
                InputProps={{
                  style: {
                    height: '136px',
                  },
                }}
              />
            )}
            <BaseTypography variant="caption" className="mt-8" style={{ margin: '16px 0' }}>
              Show in profile?<span><IOSSwitch name="is_company_description_visible" checked={formik.values.is_company_description_visible} onChange={formik.handleChange} /></span>
            </BaseTypography>
            { /* <BaseButtonGroup
              value={formik.values.is_company_description_visible}
              label="Show company description in profile"
              onChange={(value) => {
                formik.setFieldValue('is_company_description_visible', value);
              }}
            /> */ }
        </div>

        <div className={classes.submitSection}>
          <BaseButton size="small" variant="contained" color="primary" className='button save' type="submit" loading={loading}>
            {submitBtnText}
          </BaseButton>
        </div>

        
      </form>
    </div>
  );
};
